/** @format */

import { useEffect, useState } from 'react';

import { Formik } from 'formik';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';

import Loading from '../../Shared/Components/Loading';
import ModalCustom from '../../Shared/Components/ModalCustom';
import Pagination from '../../Shared/Components/Pagination';
import { error, success } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';
import { useAccess } from '../../Shared/hooks/useAccess';
import Master from '../Layouts/Master';
import ClubStatusBadge from './ClubStatusBadge';

const ClubList = () => {
  const { canReviewClubs } = useAccess();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [loading, setLoading] = useState(true);
  const [clubs, setClubs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [selectedClub, setSelectedClub] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const parseParams = queryString.parse(search);

  useEffect(() => {
    fetchClubs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const fetchClubs = async () => {
    setLoading(true);
    await useApi.get(
      `/panel/club${search}`,
      {},
      (res) => {
        setClubs(res.data.data ?? []);
        setMeta(res.data.meta ?? null);
        setLoading(false);
      },
      () => {
        setLoading(false);
        error('دریافت لیست باشگاه‌ها با خطا مواجه شد');
      },
    );
  };

  const applyFilter = (values) => {
    const params = new URLSearchParams();
    if (values.title) params.set('title', values.title);
    if (values.status !== '') params.set('status', values.status);
    const query = params.toString();
    navigate(query ? `/panel/clubs?${query}` : '/panel/clubs');
  };

  const approveClub = async (clubId) => {
    setActionLoading(true);
    await useApi.put(
      `/panel/club/${clubId}/approve`,
      {},
      () => {
        success('باشگاه با موفقیت تأیید شد');
        setSelectedClub(null);
        setActionLoading(false);
        fetchClubs();
      },
      (err) => {
        setActionLoading(false);
        error(err?.data?.message || 'تأیید باشگاه با خطا مواجه شد');
      },
    );
  };

  const rejectClub = async (clubId) => {
    if (!rejectReason || rejectReason.trim().length < 5) {
      error('لطفاً دلیل رد را وارد کنید (حداقل ۵ کاراکتر)');
      return;
    }

    setActionLoading(true);
    await useApi.put(
      `/panel/club/${clubId}/reject`,
      { reject_reason: rejectReason },
      () => {
        success('باشگاه رد شد');
        setSelectedClub(null);
        setRejectReason('');
        setActionLoading(false);
        fetchClubs();
      },
      (err) => {
        setActionLoading(false);
        error(err?.data?.message || 'رد باشگاه با خطا مواجه شد');
      },
    );
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Master>
      <div className='d-flex flex-row align-items-center border-bottom pb-3 my-3'>
        <i className='fad fa-list f-20 text-secondary mx-2'></i>
        <h1 className='text-secondary f-14 font-weight-bold mb-0'>
          لیست باشگاه‌ها
        </h1>
      </div>

      <div className='card border shadow-sm mb-3'>
        <div className='card-body py-3'>
          <Formik
            initialValues={{
              title: parseParams.title || '',
              status: parseParams.status ?? '',
            }}
            onSubmit={applyFilter}
            enableReinitialize
          >
            {({ values, handleChange, handleSubmit, resetForm }) => (
              <form onSubmit={handleSubmit} className='row g-2 align-items-end'>
                <div className='col-md-5'>
                  <label className='f-12 mb-1'>نام باشگاه</label>
                  <input
                    name='title'
                    value={values.title}
                    onChange={handleChange}
                    className='form-control form-control-sm'
                  />
                </div>
                <div className='col-md-4'>
                  <label className='f-12 mb-1'>وضعیت</label>
                  <select
                    name='status'
                    value={values.status}
                    onChange={handleChange}
                    className='form-control form-control-sm'
                  >
                    <option value=''>همه</option>
                    <option value='0'>در انتظار بررسی</option>
                    <option value='1'>تأیید شده</option>
                    <option value='2'>رد شده</option>
                  </select>
                </div>
                <div className='col-md-3 d-flex gap-2'>
                  <button type='submit' className='btn btn-info btn-sm flex-fill'>
                    جستجو
                  </button>
                  <button
                    type='button'
                    className='btn btn-outline-secondary btn-sm'
                    onClick={() => {
                      resetForm();
                      navigate('/panel/clubs');
                    }}
                  >
                    پاک کردن
                  </button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>

      <div className='table-responsive card border shadow-sm'>
        <table className='table table-hover mb-0 f-13'>
          <thead className='bg-sky-light'>
            <tr>
              <th>#</th>
              <th>نام باشگاه</th>
              <th>مالک</th>
              <th>مربی</th>
              <th>استان / شهر</th>
              <th>وضعیت</th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {clubs.length === 0 ? (
              <tr>
                <td colSpan='7' className='text-center text-muted py-4'>
                  باشگاهی یافت نشد
                </td>
              </tr>
            ) : (
              clubs.map((club, index) => (
                <tr key={club.id}>
                  <td>{index + 1}</td>
                  <td className='font-weight-bold'>{club.title}</td>
                  <td>{club.owner?.full_name || '—'}</td>
                  <td>{club.coach?.full_name || '—'}</td>
                  <td>
                    {club.province_name} / {club.city_name}
                  </td>
                  <td>
                    <ClubStatusBadge
                      status={club.status}
                      label={club.status_label}
                    />
                  </td>
                  <td>
                    <button
                      type='button'
                      className='btn btn-outline-info btn-sm f-12'
                      onClick={() => {
                        setSelectedClub(club);
                        setRejectReason('');
                      }}
                    >
                      جزئیات
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {meta?.pagination?.total_pages > 1 && (
        <Pagination
          totalPage={meta.pagination.total_pages}
          basePath={`/panel/clubs${search}`}
        />
      )}

      <ModalCustom
        modalIsOpen={!!selectedClub}
        onRequestClose={() => setSelectedClub(null)}
        styles={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(760px, 95vw)',
            maxHeight: '90vh',
            overflow: 'auto',
            background: '#fff',
            color: '#333',
            borderRadius: '12px',
            border: 'none',
            padding: 0,
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          },
        }}
      >
        {selectedClub && (
          <div className='club-detail-modal text-dark'>
            <div className='d-flex justify-content-between align-items-center border-bottom p-3'>
              <h2 className='f-15 mb-0 font-weight-bold'>{selectedClub.title}</h2>
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={() => setSelectedClub(null)}
              >
                ×
              </button>
            </div>

            <div className='p-3'>
              <div className='row g-3 f-13 mb-3'>
                <div className='col-md-6'>
                  <strong>مالک:</strong> {selectedClub.owner?.full_name}
                </div>
                <div className='col-md-6'>
                  <strong>مربی:</strong> {selectedClub.coach?.full_name}
                </div>
                <div className='col-md-6'>
                  <strong>تلفن:</strong> {selectedClub.phone}
                </div>
                <div className='col-md-6'>
                  <strong>موقعیت:</strong> {selectedClub.province_name} /{' '}
                  {selectedClub.city_name}
                </div>
                <div className='col-12'>
                  <strong>آدرس:</strong> {selectedClub.address}
                </div>
                <div className='col-12'>
                  <ClubStatusBadge
                    status={selectedClub.status}
                    label={selectedClub.status_label}
                  />
                </div>
              </div>

              {selectedClub.images?.length > 0 && (
                <div className='d-flex flex-wrap gap-2 mb-3'>
                  {selectedClub.images.map((image) => (
                    <a
                      key={image.id}
                      href={image.url}
                      target='_blank'
                      rel='noreferrer'
                      className='border rounded overflow-hidden'
                      style={{ width: 120, height: 120 }}
                    >
                      <img
                        src={image.url}
                        alt='club'
                        className='w-100 h-100 object-fit-cover'
                      />
                    </a>
                  ))}
                </div>
              )}

              {canReviewClubs() && selectedClub.status !== 1 && (
                <div className='border-top pt-3'>
                  <div className='d-flex gap-2 mb-3'>
                    <button
                      type='button'
                      className='btn btn-success btn-sm'
                      disabled={actionLoading}
                      onClick={() => approveClub(selectedClub.id)}
                    >
                      تأیید باشگاه
                    </button>
                  </div>
                  <label className='f-12 mb-1'>دلیل رد</label>
                  <textarea
                    className='form-control mb-2'
                    rows={3}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder='در صورت رد، دلیل را بنویسید...'
                  />
                  <button
                    type='button'
                    className='btn btn-danger btn-sm'
                    disabled={actionLoading}
                    onClick={() => rejectClub(selectedClub.id)}
                  >
                    رد باشگاه
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </ModalCustom>
    </Master>
  );
};

export default ClubList;
